import type { AccordionTheme } from '../components/accordion/accordion.theme';
import type { ActionCardTheme } from '../components/action_card/action_card.theme';
import type { ToastNotificationTheme } from '../components/app_notifications/toast_notification.theme';
import type { AspectRatioTheme } from '../components/aspect_ratio/aspect_ratio.theme';
import type { AttachmentTheme } from '../components/attachment/attachment.theme';
import type { AvatarTheme } from '../components/avatar/avatar.theme';
import type { BadgeNumberTheme, BadgeTextTheme } from '../components/badge';
import type { ButtonTheme } from '../components/button';
import type { CharacterCountTheme } from '../components/character_count/character_count.theme';
import type { ChipTheme } from '../components/chip/chip.theme';
import type { ColorSwatchTheme } from '../components/color_swatch/color_swatch.theme';
import type { CommonTheme } from '../components/common.theme';
import type { DialogAlertTheme } from '../components/dialog_alert';
import type { DialogModalTheme } from '../components/dialog_modal';
import type { DividerTheme } from '../components/divider';
import type { DropdownMenuTheme } from '../components/dropdown_menu/dropdown_menu.theme';
import type { FileViewerTheme } from '../components/file_viewer/file_viewer.theme';
import type {
	DescriptionTheme,
	HelpTextTheme,
	InputCheckboxTheme,
	InputRadioTheme,
	InputSelectTheme,
	InputTheme,
	LabelTheme,
	TextAreaTheme,
} from '../components/form';
import type { CommonInputTheme } from '../components/form/common_input.theme';
import type { InputComboboxTheme } from '../components/form/input_combobox/input_combobox.theme';
import type { InputDateTheme } from '../components/form/input_date/input_date.theme';
import type { InputPhoneNumberTheme } from '../components/form/input_phone_number/input_phone_number.theme';
import type { InputRatingTheme } from '../components/form/input_rating/input_rating.theme';
import type { InputSwitchTheme } from '../components/form/input_switch/input_switch.theme';
import type { InputTimeTheme } from '../components/form/input_time/input_time.theme';
import type { IconTheme } from '../components/icon';
import type { IconButtonTheme } from '../components/icon_button';
import type { NotificationAlertTheme } from '../components/notification_alert';
import type { PaginationTheme } from '../components/pagination';
import type { SkeletonLoaderTheme } from '../components/skeleton_loader';
import type { SpinnerTheme } from '../components/spinner';
import type { TabNavigationTheme } from '../components/tab_navigation';
import type { TableTheme } from '../components/table/table.theme';
import type { TableColumnMenuTheme } from '../components/table/table_column_menu/table_column_menu.theme';
import type { TextLinkTheme } from '../components/text_link';
import type { TooltipTheme } from '../components/tooltip';

// ---------------------------------------------------------------
// ^ Always use import type so they won't affect the final bundle.
// ---------------------------------------------------------------

interface Tokens {
	[key: string]: {
		cssVar: string;
		value: string;
	};
}

export interface CellulaTheme {
	tokens: Tokens;
	components: {
		common: CommonTheme;
		avatar: AvatarTheme;
		button: ButtonTheme;
		badgeNumber: BadgeNumberTheme;
		badgeText: BadgeTextTheme;
		divider: DividerTheme;
		dropdownMenu: DropdownMenuTheme;
		icon: IconTheme;
		iconButton: IconButtonTheme;
		notificationAlert: NotificationAlertTheme;
		pagination: PaginationTheme;
		spinner: SpinnerTheme;
		tooltip: TooltipTheme;
		input: InputTheme;
		textArea: TextAreaTheme;
		label: LabelTheme;
		helpText: HelpTextTheme;
		description: DescriptionTheme;
		commonInput: CommonInputTheme;
		inputSelect: InputSelectTheme;
		inputRadio: InputRadioTheme;
		inputCheckbox: InputCheckboxTheme;
		inputDate: InputDateTheme;
		inputTime: InputTimeTheme;
		inputSwitch: InputSwitchTheme;
		inputCombobox: InputComboboxTheme;
		dialogAlert: DialogAlertTheme;
		dialogModal: DialogModalTheme;
		tabNavigation: TabNavigationTheme;
		textLink: TextLinkTheme;
		characterCount: CharacterCountTheme;
		attachment: AttachmentTheme;
		fileViewer: FileViewerTheme;
		aspectRatio: AspectRatioTheme;
		accordion: AccordionTheme;
		inputPhoneNumber: InputPhoneNumberTheme;
		toastNotification: ToastNotificationTheme;
		inputRating: InputRatingTheme;
		actionCard: ActionCardTheme;
		chip: ChipTheme;
		colorSwatch: ColorSwatchTheme;
		table: TableTheme;
		tableColumnMenu: TableColumnMenuTheme;
		skeletonLoader: SkeletonLoaderTheme;
	};
}

export class ThemeProperties<T extends keyof CellulaTheme['components']> {
	private componentName: T;

	constructor(componentName: T) {
		this.componentName = componentName;
		// Empty
	}

	public getValue(themeProperty: Extract<keyof CellulaTheme['components'][T], string>, fallback?: string): string {
		const prefix = `var(--cellula-component-${this.componentName}-${themeProperty}`;

		if (fallback) {
			return prefix + `, ${fallback})`;
		}

		return prefix + ')';
	}
}
